import { useCallback, useRef, useState } from 'react';

// UNDO/REDO (Phase 1 of PLAN-pedigree-undo-redo-and-saved-views, v1.1).
//
// Session-only past[]/future[] history of the three layout states
// ({ customPositions, establishedOrder, orderLocked }) — the "layout state" primitive
// shared with Saved Views (Phase 2). The core is a plain factory (no React) so it can
// be unit-tested pure; the hook below binds it to a component.
//
// DEFERRED-COMMIT CHECKPOINTS: checkpoint() stores the pre-gesture snapshot as
// `pending` instead of pushing it immediately. The pending snapshot is committed to
// `past` (and the redo stack cleared) only once the live state is OBSERVED to differ
// from it — at the next checkpoint/undo/redo. A checkpoint followed by a content
// no-op gesture (zero-move click on a multi-selection re-writing identical pins,
// Reset Position on an unpinned node, ...) therefore never creates an undo step and
// NEVER clears the redo stack — the v1.1 §2.1 dedupe, without any call-site
// content inspection. Engine auto-capture merges land between checkpoints and are
// deliberately not checkpointed, so they fold into the adjacent user step (§2.3).

// Geometry-relevant pin fields, as written by the Canvas/ChartBox pin writers.
// Object.is so historical NaN pins (G-1-01391) still compare stable.
const PIN_FIELDS = ['uid', 'vx', 'vy', 'x', 'y', 'gx', 'gy'];

function cloneMap(m) {
  return m instanceof Map ? new Map(m) : new Map();
}

export function snapshotLayoutState(state) {
  return {
    customPositions: cloneMap(state?.customPositions),
    establishedOrder: cloneMap(state?.establishedOrder),
    orderLocked: !!state?.orderLocked
  };
}

function pinsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  for (const f of PIN_FIELDS) {
    if (!Object.is(a[f], b[f])) return false;
  }
  return true;
}

function mapsEqual(a, b, valueEquals) {
  const sizeA = a instanceof Map ? a.size : 0;
  const sizeB = b instanceof Map ? b.size : 0;
  if (sizeA !== sizeB) return false;
  if (sizeA === 0) return true;
  for (const [k, v] of a) {
    if (!b.has(k)) return false;
    if (!valueEquals(v, b.get(k))) return false;
  }
  return true;
}

export function pinMapsEqual(a, b) {
  return mapsEqual(a, b, pinsEqual);
}

export function orderMapsEqual(a, b) {
  return mapsEqual(a, b, (x, y) => Object.is(x, y));
}

export function layoutStatesEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (!!a.orderLocked !== !!b.orderLocked) return false;
  if (!orderMapsEqual(a.establishedOrder, b.establishedOrder)) return false;
  return pinMapsEqual(a.customPositions, b.customPositions);
}

export function createLayoutHistory({ capacity = 50 } = {}) {
  let past = [];
  let future = [];
  let pending = null;

  const pushPast = (snap) => {
    past.push(snap);
    if (past.length > capacity) past.shift(); // drop oldest (§2.3 depth cap)
  };

  // Commit the pending checkpoint iff the gesture that took it actually changed
  // something; discard it otherwise. Committing clears the redo stack (a new real
  // action invalidates redo).
  const commitPendingIfChanged = (live) => {
    if (!pending) return;
    if (!layoutStatesEqual(pending, live)) {
      pushPast(pending);
      future = [];
    }
    pending = null;
  };

  return {
    // Call immediately BEFORE a user-intent mutation of the layout states.
    checkpoint(liveState) {
      const live = snapshotLayoutState(liveState);
      commitPendingIfChanged(live);
      pending = live;
    },

    // Call immediately BEFORE an AUTOMATIC layout-state write (engine settled-order
    // auto-capture, one-shot legacy-pin upgrade, hygiene sweeps). Resolves any armed
    // pending against the pre-write live state so the pending is attributed to its
    // own gesture ONLY: if the gesture mutated, it commits now (the automatic drift
    // then folds forward, uncheckpointed); if it was a no-op, it is discarded —
    // without this, the automatic drift would be misattributed to the no-op gesture,
    // committing a phantom step and clearing the redo stack (e.g. after a person
    // add re-ranks a generation while a snap-back click's pending is still armed).
    resolvePending(liveState) {
      commitPendingIfChanged(snapshotLayoutState(liveState));
    },

    // Rewrite the stored order of EVERY snapshot the history holds (past, future
    // and an armed pending) through `fn(order) -> order`. A structural edit prunes
    // the ranks it made stale from the live order (TGCLOUD-1786, structuralEditOrder.js)
    // — but undo is layout-only and leaves the members as they are, so a snapshot
    // still holding such a rank would put it back against the new structure, and
    // the autosave would save the wedge. The chart runs the same prune here.
    // Pins and the lock are untouched; `fn` may return its argument unchanged.
    rewriteOrders(fn) {
      const rewrite = (snap) =>
        snap
          ? { ...snap, establishedOrder: cloneMap(fn(snap.establishedOrder)) }
          : snap;
      past = past.map(rewrite);
      future = future.map(rewrite);
      pending = rewrite(pending);
    },

    // Drop everything (past, future, pending). Used when the pedigree IDENTITY
    // changes under a mounted chart (route param swap re-uses the component) —
    // undoing another family's snapshot onto this one must be impossible.
    clear() {
      past = [];
      future = [];
      pending = null;
    },

    // Returns the snapshot to restore, or null when there is nothing to undo.
    // Entries equal to the live state carry no restorable change — skip them.
    undo(liveState) {
      const live = snapshotLayoutState(liveState);
      commitPendingIfChanged(live);
      let snap = null;
      while (past.length) {
        const top = past.pop();
        if (!layoutStatesEqual(top, live)) {
          snap = top;
          break;
        }
      }
      if (!snap) return null;
      future.unshift(live);
      return snap;
    },

    redo(liveState) {
      const live = snapshotLayoutState(liveState);
      // A real mutation since the last checkpoint invalidates the redo stack.
      commitPendingIfChanged(live);
      let snap = null;
      while (future.length) {
        const first = future.shift();
        if (!layoutStatesEqual(first, live)) {
          snap = first;
          break;
        }
      }
      if (!snap) return null;
      pushPast(live);
      return snap;
    },

    canUndo(liveState) {
      if (pending && !layoutStatesEqual(pending, liveState)) return true;
      return past.length > 0;
    },

    canRedo(liveState) {
      // An uncommitted mutated pending will clear `future` on its commit.
      if (pending && !layoutStatesEqual(pending, liveState)) return false;
      return future.length > 0;
    },

    // Introspection for tests.
    depth() {
      return { past: past.length, future: future.length };
    }
  };
}

/**
 * Binds a layout history to a component.
 *
 * @param liveStateRef ref holding the CURRENT { customPositions, establishedOrder,
 *   orderLocked } — read at event time (checkpoint/undo/redo run in event handlers,
 *   after the previous commit has synced the ref).
 * @param renderState the same three states as render-scope values — canUndo/canRedo
 *   must be computed against the state of THIS render, not the ref (the ref is only
 *   re-synced in a layout effect, so it lags by one frame right after a set*).
 */
export function useLayoutHistory(liveStateRef, renderState, options = {}) {
  const [, setVersion] = useState(0);
  const historyRef = useRef(null);
  if (historyRef.current === null) {
    historyRef.current = createLayoutHistory(options);
  }
  const history = historyRef.current;
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const checkpoint = useCallback(() => {
    history.checkpoint(liveStateRef.current);
    bump();
  }, [history, liveStateRef, bump]);

  const undo = useCallback(() => {
    const snap = history.undo(liveStateRef.current);
    bump();
    return snap;
  }, [history, liveStateRef, bump]);

  const redo = useCallback(() => {
    const snap = history.redo(liveStateRef.current);
    bump();
    return snap;
  }, [history, liveStateRef, bump]);

  const resolvePending = useCallback(() => {
    history.resolvePending(liveStateRef.current);
    bump();
  }, [history, liveStateRef, bump]);

  const clear = useCallback(() => {
    history.clear();
    bump();
  }, [history, bump]);

  const rewriteOrders = useCallback(
    (fn) => {
      history.rewriteOrders(fn);
      bump();
    },
    [history, bump]
  );

  return {
    checkpoint,
    undo,
    redo,
    resolvePending,
    clear,
    rewriteOrders,
    canUndo: history.canUndo(renderState),
    canRedo: history.canRedo(renderState)
  };
}
